import { useCallback, useState, useContext, useMemo, useEffect } from "react";
import type { Site } from "@/types/site.types";
import { TopBar } from "./shared/TopBar";
import { Button } from "@stackje/shared-components/ui/button";
import { ChevronLeft, Loader2, RocketIcon, Globe } from "lucide-react";
import { Label } from "@stackje/shared-components/ui/label";
import {
  ClearableInput,
  ClearableTextarea,
} from "@stackje/shared-components/ui/clearable-fields";
import { BottomBar } from "./shared/BottomBar";
import { UnsavedChangesDialog } from "./shared/UnsavedChangesDialog";
import { SitesRepository } from "@/repositories/SitesRepository";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { deepEqual, useNavigate } from "@tanstack/react-router";
import { useInvalidateSiteCache } from "@/hooks/useSiteQuery";
import { ColorSchemeSelector, type ColorSchemeItem } from "./shared/ColorSchemeSelector";
import { FontSetSelector } from "./shared/FontSetSelector";
import { ColorScheme, ColorSchemeLabels, type ColorSchemeId } from "@stackje/common/colorScheme";
import { getTailwindColor } from "@/utils/tailwind-utils";
import { generateNewColors } from "@/utils/color-scheme-utils";
import type { FontSetId } from "@stackje/common/fontSet";
import { FormImageUploader } from "./shared/FormImageUploader";
import type { SelectedImageData } from "./shared/ImageEditor";
import { AuthContext } from "@/context/AuthContext";
import { useSubscriptionQuery } from "@/hooks/useSubscriptionQuery";
import { ProBadge } from "./shared/ProBadge";
import { CustomColorPickerDialog } from "./shared/CustomColorPickerDialog";

export function SiteDetailsPage({ site }: { site: Site }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const invalidateSiteCache = useInvalidateSiteCache();
  const authContext = useContext(AuthContext);
  const { data: subscription } = useSubscriptionQuery(authContext?.user?.id);
  const isPro = subscription?.plan_type === 'pro';
  const [title, setTitle] = useState(site.config?.title ?? "");
  const [description, setDescription] = useState(
    site.config?.description ?? ""
  );
  const [hasFormInputChanges, setHasFormInputChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedColorScheme, setSelectedColorScheme] = useState<ColorSchemeId>(
    site.siteDetails?.colorSchemeId ?? "natural"
  );
  const [selectedFontSet, setSelectedFontSet] = useState<FontSetId>(
    site.config?.fontSetId ?? "modern"
  );
  const [customColors, setCustomColors] = useState<string[] | null>(
    site.siteDetails?.customColors ?? null
  );
  const [isCustomColorSelected, setIsCustomColorSelected] = useState(
    site.siteDetails?.colorSchemeId === "custom" && !!site.siteDetails?.customColors
  );
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [favicon, setFavicon] = useState<SelectedImageData | null>(
    site.config?.favicon?.src ? {
      fullUrl: site.config.favicon.src,
      previewUrl: site.config.favicon.src,
    } : null
  );
  const [gtag, setGtag] = useState(site.config?.gtag ?? "");

  const setValueAndMarkDirty = useCallback(function <ValueType>(setter: (value: ValueType) => void) {
    return (value: ValueType) => {
      setter(value);
      setHasFormInputChanges(true);
    };
  }, [setHasFormInputChanges]);

  // Reset custom colors if user loses pro access
  useEffect(() => {
    if (!isPro && isCustomColorSelected) {
      setIsCustomColorSelected(false);
      setCustomColors(null);
      setSelectedColorScheme("natural");
    }
  }, [isPro, isCustomColorSelected]);


  const handleSchemeSelect = (schemeId: string) => {
    if (schemeId === "custom") {
      if (!isPro) {
        // Redirect non-pro users to subscription page
        navigate({ to: '/subscription' });
        return;
      }
      // Open custom color picker for pro users
      setShowCustomPicker(true);
    } else {
      setIsCustomColorSelected(false);
      setCustomColors(null);
      setSelectedColorScheme(schemeId as ColorSchemeId);
      setHasFormInputChanges(true);
    }
  };

  const handleCustomColorPickerSelect = (colors: string[]) => {
    setCustomColors(colors);
    setIsCustomColorSelected(true);
    setSelectedColorScheme("custom");
    setShowCustomPicker(false);
    setHasFormInputChanges(true);
  };

  const handleBackClick = () => {
    history.back();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let newPages = site.config?.pages ?? [];
    if (selectedColorScheme !== site.siteDetails?.colorSchemeId || (isCustomColorSelected && !deepEqual(customColors, site.siteDetails?.customColors))) {
      // regenerate colors for each card in each page if the color scheme was changed
      newPages = newPages.map((page) => ({
        ...page,
        cards: page.cards.map((card) => {
          if ('colors' in card) {
            if (isCustomColorSelected && customColors && isPro) {
              // Use generateNewColors with custom color array (pro only)
              return {
                ...card,
                colors: generateNewColors(card.colors, customColors)
              };
            } else if (selectedColorScheme !== "custom") {
              // Use predefined color scheme (exclude custom since it has empty array)
              return {
                ...card,
                colors: generateNewColors(card.colors, ColorScheme[selectedColorScheme as ColorSchemeId])
              };
            }
          }
          return card;
        }),
      }));
    }

    try {
      const siteDetails = {
        ...site.siteDetails,
        title,
        description,
        fontSetId: selectedFontSet,
        ...(isCustomColorSelected && customColors && isPro
          ? {
              colorSchemeId: "custom" as const,
              customColors
            }
          : {
              colorSchemeId: selectedColorScheme,
           }
        ),
      };

      const { error } = await SitesRepository.update(site.id, site.userId, {
        siteDetails,
        config: {
          ...site.config,
          title,
          description,
          pages: newPages,
          fontSetId: selectedFontSet,
          favicon: favicon?.fullUrl ? { 
            src: favicon.fullUrl, 
            alt: "Site favicon" 
          } : undefined,
          gtag: isPro && gtag.trim() ? gtag.trim() : undefined,
        },
      });
      if (error) {
        throw error;
      }

      invalidateSiteCache(site.userId);

      toast({
        description: "Site details updated successfully",
      });
      navigate({ to: "/" });
    } catch (e) {
      captureErrorAndToast(e, toast, {
        description: `${e}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const colorSchemes: ColorSchemeItem[] = useMemo(() => {
    // Build base schemes (excluding 'custom' placeholder)
    const baseSchemes = Object.entries(ColorScheme)
      .filter(([key]) => key !== 'custom')
      .map(([key, colors]) => ({
        id: key,
        name: ColorSchemeLabels[key as ColorSchemeId],
        visual: colors.map((color) => {
          const colorName = color.replace(/-[0-9]+$/, "");
          if (colorName === "white" || colorName === "black") {
            return { color: colorName };
          }

          const colorNumber = color.match(/-[0-9]+$/)?.[0]?.replace("-", "");
          const colorValue = getTailwindColor(colorName)[colorNumber ?? "500"];
          return { color: colorValue };
        }),
      }));
    
    const visual = customColors
      ? customColors.map((color) => ({ color }))
      : site.siteDetails?.customColors
        ? site.siteDetails.customColors.map((color) => ({ color }))
        : [
            // Default gradient preview when no custom colors set
            { color: "#E9D5FF" },
            { color: "#93C5FD" },
            { color: "#86EFAC" },
            { color: "#FDE047" },
            { color: "#FCA5A5" },
          ];

    // Always add custom option, but make it conditional for non-pro users
    const customScheme: ColorSchemeItem = {
      id: 'custom',
      name: '✨ Custom',
      visual,
      disabled: !isPro,
    };

    return [...baseSchemes, customScheme];
  }, [customColors, site.siteDetails?.customColors, isPro]);

  return (
    <div className="pt-6 pb-6 lg:pt-10 lg:pb-10">
      <TopBar>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1>Site Details</h1>
        <div className="w-8" />
      </TopBar>

      <div className="space-y-4 px-8 py-16 max-w-md mx-auto w-full">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="favicon">Site Icon (Favicon)</Label>
              <ProBadge isPro={isPro} />
            </div>
            <FormImageUploader
              currentImage={favicon}
              onImageChange={isPro ? setValueAndMarkDirty(setFavicon) : () => {}}
              targetCropWidth={32}
              targetCropHeight={32}
              allowFreeCrop={false}
              imagePreviewClassName="max-w-[32px] h-[32px] rounded border object-contain bg-muted"
              dropzoneClassName="p-4 border-2 border-dashed rounded-md transition-colors"
              outputFormat="png"
              disabled={!isPro}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Site Title</Label>
            <ClearableInput
              id="title"
              name="title"
              value={title}
              onChange={setValueAndMarkDirty(setTitle)}
              placeholder="Enter site title"
              maxLength={60}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Site Description</Label>
            <ClearableTextarea
              id="description"
              name="description"
              value={description}
              onChange={setValueAndMarkDirty(setDescription)}
              placeholder="Enter site description"
              maxLength={160}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="gtag">Google Analytics ID</Label>
              <ProBadge isPro={isPro} />
            </div>
            <ClearableInput
              id="gtag"
              name="gtag"
              value={gtag}
              onChange={setValueAndMarkDirty(setGtag)}
              placeholder="G-XXXXXXXXXX"
              disabled={!isPro}
            />
          </div>

          {/* Custom Domain Configuration */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label>Custom Domain</Label>
              <ProBadge isPro={isPro} />
            </div>
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/custom-domain" })}
              disabled={!isPro}
              className="flex items-center gap-2"
            >
              <Globe className="h-4 w-4" />
              {site.customDomain ? "Manage Custom Domain" : "Configure Custom Domain"}
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Color Scheme</Label>
            <ColorSchemeSelector
              schemes={colorSchemes}
              title={undefined}
              selectedSchemeId={isCustomColorSelected ? 'custom' : selectedColorScheme}
              onSchemeSelect={handleSchemeSelect}
            />
          </div>

          <div className="flex flex-col gap-2">
            <FontSetSelector
              title="Font Set"
              selectedFontSetId={selectedFontSet}
              onFontSetSelect={setValueAndMarkDirty(setSelectedFontSet)}
            />
          </div>

          <BottomBar>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <RocketIcon className="h-5 w-5 mr-2" />
                  Save
                </>
              )}
            </Button>
          </BottomBar>
        </div>
      </div>

      <UnsavedChangesDialog
        isDirty={hasFormInputChanges && !isSubmitting}
      />

      <CustomColorPickerDialog
        open={showCustomPicker}
        onOpenChange={setShowCustomPicker}
        onColorSchemeSelect={handleCustomColorPickerSelect}
        initialColors={customColors || site.siteDetails?.customColors || undefined}
      />
    </div>
  );
}
